import { useToastController } from "@tamagui/toast";
import { useContext } from "react";

import {
  useGetClinicRequest,
  useGetOrCreateUserProgression,
} from "@/api/generated";
import {
  type ClinicRequestContent,
  type CuraiRedirectContent,
  EncounterStartType,
  type PartnerIntentRequest,
  type ProductOption,
  ProgramGroup,
  type RxAgentProgressionRequest,
  UserProgressionStateModel,
  type VisitType,
} from "@/api/generated/models";
import { ClinicRequestStatus } from "@/api/generated/models/ClinicRequestStatus";
import { AssistantClientEvent } from "@/api/model/Conversation";
import { useGetVisitOptionsForAFlow } from "@/api/queries/visitOptions";
import RxIcon from "@/assets/icons/RXIcon";
import { Button, ChatNote, LayoutContext } from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import AccessClinicRequest from "@/conversation/AssistantMessage/ClinicRequest/AccessClinicRequest";
import CuraiRedirect from "@/conversation/AssistantMessage/ClinicRequest/CuraiRedirect";
import OfferCard, {
  useOfferVariation,
} from "@/conversation/AssistantMessage/ClinicRequest/OfferCard";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useAnalytics } from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import { useSubmitAsyncClinicRequest } from "@/hooks/useSubmitAsyncClinicRequest";
import { captureException } from "@/sentry";
import { useGetColor } from "@/theme/helpers";
import { AssistantMessageTypes } from "@/types";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";

export const ClinicRequestIcon = () => {
  const iconColor = useGetColor("$iconCta");
  const buttonBg = useGetColor("$fillPrimary");
  return <RxIcon color={iconColor} backgroundColor={buttonBg} />;
};
const CONFLICT = "conflict";

const buildAsyncProgressionRequest = (
  clinicRequestId: string,
  conversationId: string,
  partnerIntent: PartnerIntentRequest | null | undefined,
  chosenProductOption?: ProductOption,
  visitType?: VisitType,
): RxAgentProgressionRequest => {
  const partner = partnerIntent ? { partner_intent: partnerIntent } : {};
  return {
    flow_name: "rx_agent",
    clinic_request_id: clinicRequestId,
    conversation_id: conversationId,
    metadata: {
      // Still sent here until we know nothing depends on the metadata copy.
      clinic_request_id: clinicRequestId,
      ...partner,
      ...(chosenProductOption && {
        payment_product_option: chosenProductOption,
      }),
      ...(visitType && { payment_visit_type: visitType }),
    },
  };
};

const VOIDED = {
  title: "This request has been closed",
  subtitle:
    "Continue below to connect with a clinician who will review your case.",
} as const;

const UPDATED = {
  title: "Your doctor has been updated",
  subtitle: "We've shared this update with your Curai Health clinician.",
} as const;

const EXPIRED = {
  title: "Request expired",
  subtitle: "Please start a new chat if you still need care.",
} as const;

// Under `keep-conversations-open` this thread carries the next visit, so there is nowhere to send
// the user — ask them what they need instead.
const EXPIRED_KEEP_OPEN = {
  ...EXPIRED,
  subtitle: "Tell me what you need and I can set up a new visit.",
} as const;

const CLINIC_REQUEST_CONTENT = {
  [ClinicRequestStatus.created]: {
    title: "Send request for a prescription",
    subtitle:
      "After this request is sent, a licensed clinician will review your information and decide whether to approve the prescription or recommend next steps.  You can expect to get a response from one of our clinicians in 30-60 minutes.",
  },
  [ClinicRequestStatus.complete]: {
    title: "Your prescription request has been reviewed",
    subtitle:
      "Next steps will be sent to you via follow-up conversation and / or SMS",
  },
  [ClinicRequestStatus.voided]: VOIDED,
  [ClinicRequestStatus.updated]: UPDATED,
  [ClinicRequestStatus.in_progress]: {
    title: "Your prescription request is now with our clinical team.",
    subtitle:
      "It usually takes about 30–60 minutes for a licensed clinician to review requests. You'll get notified by SMS as soon as there's an update.",
  },
  [CONFLICT]: {
    title: "You currently have an ongoing request or visit with our team",
    subtitle:
      "Once updated, a licensed clinician will review your information and prescription.",
  },
} as const;

const CLINIC_REQUEST_RX_RENEWAL_CONTENT = {
  [ClinicRequestStatus.created]: {
    title: "Send your refill request to a clinician",
    subtitle:
      "A licensed clinician will review your request and send your prescription to your pharmacy if approved. This usually takes 30-60 minutes. We will send you an SMS to confirm your next steps.",
  },
  [ClinicRequestStatus.complete]: {
    title: "Your prescription refill request has been reviewed",
    subtitle:
      "Next steps will be sent to you via follow-up conversation and / or SMS",
  },
  [ClinicRequestStatus.voided]: VOIDED,
  [ClinicRequestStatus.updated]: UPDATED,
  [ClinicRequestStatus.in_progress]: {
    title: "Your prescription refill request is now with our clinical team.",
    subtitle:
      "It usually takes about 30–60 minutes for a licensed clinician to review requests. You'll get notified by SMS as soon as there's an update.",
  },
  [CONFLICT]: {
    title: "You currently have an ongoing request or visit with our team",
    subtitle:
      "Once updated, a licensed clinician will review your information and prescription.",
  },
} as const;

const CLINIC_REQUEST_ASYNC_DX_CONTENT = {
  [ClinicRequestStatus.created]: {
    title: "Your case summary",
    subtitle:
      "Once submitted, a board-certified clinician will review your information and create a personalized care plan.",
  },
  [ClinicRequestStatus.complete]: {
    title: "Your case summary has been reviewed",
    subtitle:
      "Next steps will be sent to you via follow-up conversation and / or SMS",
  },
  [ClinicRequestStatus.voided]: VOIDED,
  [ClinicRequestStatus.updated]: UPDATED,
  [ClinicRequestStatus.in_progress]: {
    title: "Your case summary is now with our clinical team.",
    subtitle:
      "It usually takes about 30–60 minutes for a licensed clinician to review requests. You'll get notified by SMS as soon as there's an update.",
  },
  [CONFLICT]: {
    title: "You currently have an ongoing request or visit with our team",
    subtitle:
      "Once updated, a licensed clinician will review your information.",
  },
} as const;

export interface ClinicRequestProps extends AssistantClientEvent {
  content: ClinicRequestContent;
  type: AssistantMessageTypes.CLINIC_REQUEST;
  clinicRequestId?: string;
}

const AsyncClinicRequest = ({
  content,
  clinicRequestId,
  conversationId: eventConversationId,
}: ClinicRequestProps) => {
  const urlConversationId = useCurrentConversationId();
  // The conversation that owns the request, which in a group stream is not always the url's.
  const conversationId = eventConversationId ?? urlConversationId;
  const { track } = useAnalytics();
  const encounterType = content.type || EncounterStartType.ASYNC_RX;
  const { value: usesPaymentProgression, loading: isPaywallFlagLoading } =
    useFeatureFlag("chai-paywall-user-progression", false);
  const { value: asyncVisitsDisabled, loading: isAsyncVisitsFlagLoading } =
    useFeatureFlag("async-visits-disabled", false);
  const { value: keepConversationsOpen, loading: isKeepOpenLoading } =
    useFeatureFlag("keep-conversations-open", false);
  const { show: showToast } = useToastController();
  const requestExpiredToast = keepConversationsOpen
    ? "This request expired. Tell me what you need and I can set up a new visit."
    : "This request expired. Please start a new chat to continue.";
  const {
    data: clinicRequest,
    isLoading: isClinicRequestLoading,
    refetch: refetchClinicRequest,
  } = useGetClinicRequest(conversationId as string, clinicRequestId as string, {
    query: {
      enabled: Boolean(conversationId && clinicRequestId),
    },
  });
  const { setModalContent } = useContext(LayoutContext);
  const {
    mutateAsync: getOrCreateUserProgression,
    isPending: isCreatingProgression,
  } = useGetOrCreateUserProgression({
    mutation: { meta: { suppressErrorToast: true } },
  });
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { data: visitOptionsData, isLoading: isVisitOptionsLoading } =
    useGetVisitOptionsForAFlow({
      clinicRequestId,
      enabled:
        usesPaymentProgression &&
        clinicRequest?.status === ClinicRequestStatus.created,
      flowName: "rx_agent",
    });
  const { productOptions = [], visitOptions = [] } = visitOptionsData ?? {};
  const { offerVariation, partnerName } = useOfferVariation(visitOptions);
  const {
    handleComplete,
    hasOngoingClinicVisit,
    isClinicStatusLoading,
    isPending: isSubmittingClinicRequest,
  } = useSubmitAsyncClinicRequest({
    conversationId,
    clinicRequestId,
    encounterType: encounterType as EncounterStartType,
    visitType: clinicRequest?.visit_type ?? undefined,
  });

  const startProgression = async (chosenProductOption?: ProductOption) => {
    if (isCreatingProgression) return;
    let userProgression: UserProgressionStateModel | undefined;
    const partnerIntent = await getLocalPartnerIntent();
    const visitType =
      usesPaymentProgression && visitOptions.length === 1
        ? visitOptions[0].visit_type
        : undefined;
    const oneTimeProductOption = visitType
      ? productOptions.find((option) => option.type === "one-time")?.code
      : undefined;
    const productOption = chosenProductOption ?? oneTimeProductOption;
    if (usesPaymentProgression && (!visitType || !productOption)) {
      // This case shouldn't happen, but log if it does, it indicates an api error
      captureException("Missing required async payment progression selection", {
        extra: {
          clinicRequestId,
          visitType,
          productOption,
          visitOptionCount: visitOptions.length,
          productOptionCount: productOptions.length,
        },
      });
      showToast(
        "We couldn’t start your visit. Please try again, or contact support if the problem continues.",
      );
      return;
    }
    track(AD_CONVERSION_EVENTS.startAVisit, {
      conversation_id: conversationId,
      clinic_request_id: clinicRequestId,
      encounter_type: encounterType,
      visit_type: visitType ?? encounterType.toLowerCase(),
    });
    try {
      userProgression = await getOrCreateUserProgression({
        data: buildAsyncProgressionRequest(
          clinicRequestId as string,
          conversationId as string,
          partnerIntent,
          productOption,
          visitType,
        ),
      });
    } catch (error) {
      if (isClinicRequestExpiredError(error)) {
        await refetchClinicRequest();
        showToast(requestExpiredToast);
      } else {
        showToast("Something went wrong. Please try again.");
      }
      return;
    }
    setModalContent(() => (props: any) => (
      <UserProgressionFlow
        initialProgression={userProgression}
        handleComplete={handleComplete}
        {...props}
      />
    ));
  };
  if (!clinicRequestId) {
    return null;
  }

  if (
    isClinicRequestLoading ||
    isClinicStatusLoading ||
    isPaywallFlagLoading ||
    isAsyncVisitsFlagLoading ||
    isKeepOpenLoading ||
    isVisitOptionsLoading
  ) {
    return <ChatNote.Skeleton />;
  }

  const hasBeenSubmitted =
    clinicRequest?.status !== ClinicRequestStatus.created;

  if (clinicRequest?.is_expired && !hasBeenSubmitted) {
    const expired = keepConversationsOpen ? EXPIRED_KEEP_OPEN : EXPIRED;
    return (
      <ChatNote.Active
        Icon={ClinicRequestIcon}
        title={expired.title}
        subtitle={expired.subtitle}
      />
    );
  }

  const isEntitledToPresentedVisit = visitOptions.some(
    (option) => option.entitlement_state.entitled,
  );
  const showOfferCard =
    // We are limiting only the Async RX product to use the offer card
    // Async DX and Rx renewal continue to use the pre-existing clinic request card
    encounterType === EncounterStartType.ASYNC_RX &&
    usesPaymentProgression &&
    !hasBeenSubmitted &&
    !hasOngoingClinicVisit &&
    visitOptions.length > 0 &&
    !isEntitledToPresentedVisit;

  if (showOfferCard) {
    return (
      <OfferCard
        offerVariation={offerVariation}
        partnerName={partnerName}
        productOptions={productOptions}
        onStartVisit={startProgression}
      />
    );
  }

  // If visits are turned off, don't offer to send a new request — show that the
  // clinic is currently unavailable. The gate only applies to non-paywall
  // users; paywall users are handled by the offer card / progression flow above
  // and are never shown this card. Already-submitted and ongoing requests fall
  // through to their existing status cards below.
  if (
    asyncVisitsDisabled &&
    !usesPaymentProgression &&
    !hasBeenSubmitted &&
    !hasOngoingClinicVisit
  ) {
    return (
      <ChatNote.Active
        Icon={ClinicRequestIcon}
        title="Clinic currently unavailable"
        subtitle="Our clinic isn't accepting new requests right now. Please check back soon."
        button={
          <Button mode="primary" disabled={true}>
            Unavailable
          </Button>
        }
      />
    );
  }

  const contentMap =
    encounterType === EncounterStartType.ASYNC_DX
      ? CLINIC_REQUEST_ASYNC_DX_CONTENT
      : encounterType === EncounterStartType.RX_RENEWAL
        ? CLINIC_REQUEST_RX_RENEWAL_CONTENT
        : CLINIC_REQUEST_CONTENT;

  const getClinicRequestContent = (status?: ClinicRequestStatus) => {
    if (status === ClinicRequestStatus.voided) {
      return contentMap[ClinicRequestStatus.voided];
    }

    if (status === ClinicRequestStatus.updated) {
      return contentMap[ClinicRequestStatus.updated];
    }

    if (
      status === ClinicRequestStatus.complete ||
      status === ClinicRequestStatus.cancelled
    ) {
      return contentMap[ClinicRequestStatus.complete];
    }

    if (
      status === ClinicRequestStatus.pending ||
      status === ClinicRequestStatus.in_progress
    ) {
      return contentMap[ClinicRequestStatus.in_progress];
    }

    if (hasOngoingClinicVisit) {
      return contentMap[CONFLICT];
    }

    return contentMap[ClinicRequestStatus.created];
  };

  const { title, subtitle } = getClinicRequestContent(clinicRequest?.status);

  const buttonText = hasOngoingClinicVisit
    ? "Update my doctor"
    : "Send to clinician"; // this copy is used by async-rx e2e test pls update if changed

  // Updating an open visit needs no progression — nothing new to pay for.
  const handlePress = hasOngoingClinicVisit
    ? () => handleComplete()
    : () => startProgression();

  return (
    <ChatNote.Active
      Icon={ClinicRequestIcon}
      title={title}
      subtitle={subtitle}
      button={
        clinicRequest?.status === ClinicRequestStatus.updated ? (
          <Button mode="primary" disabled={true}>
            Update complete
          </Button>
        ) : hasBeenSubmitted ? null : (
          <Button
            mode="primary"
            onPress={handlePress}
            $lg={{ alignSelf: "center" }}
            loading={isCreatingProgression}
            disabled={isSubmittingClinicRequest}
          >
            {buttonText}
          </Button>
        )
      }
    />
  );
};

// VISIT clinic requests use the visit card path without mounting async request hooks.
const VisitClinicRequest = ({
  content,
  clinicRequestId,
  ...rest
}: ClinicRequestProps) => {
  const urlConversationId = useCurrentConversationId();
  // The conversation that owns the request, which in a group stream is not always the url's.
  const conversationId = rest.conversationId ?? urlConversationId;
  const { data: clinicRequest, isLoading } = useGetClinicRequest(
    conversationId as string,
    clinicRequestId as string,
    {
      query: { enabled: Boolean(conversationId && clinicRequestId) },
    },
  );
  const { value: keepConversationsOpen, loading: isKeepOpenLoading } =
    useFeatureFlag("keep-conversations-open", false);

  // Gated so an expired card never paints pre-flag copy and then swaps.
  if (isLoading || isKeepOpenLoading) {
    return <ChatNote.Skeleton />;
  }

  const hasBeenSubmitted =
    clinicRequest?.status !== ClinicRequestStatus.created;

  if (
    clinicRequest?.program?.group === ProgramGroup.access &&
    clinicRequest.status === ClinicRequestStatus.voided
  ) {
    return (
      <ChatNote.Active
        Icon={ClinicRequestIcon}
        title="Enrollment closed"
        subtitle="This enrollment request has been closed."
      />
    );
  }

  if (clinicRequest?.is_expired && !hasBeenSubmitted) {
    const expired = keepConversationsOpen ? EXPIRED_KEEP_OPEN : EXPIRED;
    return (
      <ChatNote.Active
        Icon={ClinicRequestIcon}
        title={expired.title}
        subtitle={expired.subtitle}
      />
    );
  }

  const actionTaken =
    clinicRequest?.status === ClinicRequestStatus.updated
      ? "visit_updated"
      : !clinicRequest || clinicRequest.status === ClinicRequestStatus.created
        ? null
        : "visit_started";
  const curaiRedirectContent = {
    ...content,
    action_taken: actionTaken,
  } as CuraiRedirectContent;

  if (clinicRequest?.program?.group === ProgramGroup.access) {
    return (
      <AccessClinicRequest
        {...rest}
        type={AssistantMessageTypes.CURAI_REDIRECT}
        content={curaiRedirectContent}
        clinicRequestId={clinicRequestId}
        clinicConversationId={clinicRequest.created_clinic_conversation_id}
        requestStatus={clinicRequest.status}
      />
    );
  }

  return (
    <CuraiRedirect
      {...rest}
      type={AssistantMessageTypes.CURAI_REDIRECT}
      content={curaiRedirectContent}
      clinicRequestId={clinicRequestId}
      clinicConversationId={clinicRequest?.created_clinic_conversation_id}
    />
  );
};

const ClinicRequest = (props: ClinicRequestProps) =>
  props.content.type === "VISIT" ? (
    <VisitClinicRequest {...props} />
  ) : (
    <AsyncClinicRequest {...props} />
  );

export default ClinicRequest;
